import { Injectable } from '@angular/core';
import { HttpService } from 'services/http/http.service';
import { downloadFile, createFileUploadHeaders } from 'utils/helpers';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { generateGuid, ShowIfHttpError } from '@certemy/common';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { LoaderService, MessageQueryService } from '@certemy/ui-components';
import _get from 'lodash-es/get';

@Injectable()
export class FileService {
  constructor(
    private http: HttpService,
    private loaderService: LoaderService,
    private messageService: MessageQueryService,
  ) {}

  @ShowIfHttpError('Cannot get file')
  download(name: string) {
    return this.http
      .get(`/file/${name}`, { responseType: 'arraybuffer' })
      .pipe(
        tap((file: ArrayBuffer) =>
          downloadFile(file, name, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'),
        ),
      );
  }

  @ShowIfHttpError('Cannot upload file')
  upload(file: File, fileName = '', params = { skipLoaderLock: true }): Observable<{ link: string }[]> {
    const formData = new FormData();
    const options = { ...createFileUploadHeaders(), params };
    formData.append('file', file, fileName);

    return this.http.post(`/file`, formData, options);
  }

  filesUpload(files: File[]) {
    return forkJoin(
      files.map(file =>
        this.loaderService.wrapObservable(this.upload(file, file.name)).pipe(
          catchError(err => {
            const errorDetails = _get(err, 'body.error_details', 'Cannot upload file');
            this.messageService.addErrorMessage(errorDetails);
            return of('error');
          }),
        ),
      ),
    ).pipe(
      switchMap(attachments => {
        const values = attachments.reduce((arr: any[], attachment, index) => {
          if (attachment === 'error') {
            return arr;
          }
          const { name, size } = files[index];
          const { link } = attachment[0];
          const value: any = { link, name, size, id: generateGuid() };
          return [...arr, value];
        }, []) as any[];
        if (values.length !== files.length) {
          return throwError('The file was not uploaded to the server. Please try again later');
        }
        return of(values);
      }),
    );
  }
}
